import { performanceDiagnostics } from '@/diagnostics/browserDiagnostics';
import { orderedEdit } from '@/simulation/native/authority';
import { makeAutoObservable } from 'mobx';
import type { IHistoryModel } from '@/interfaces/models';

export interface Transaction {
  actions: Action[];
  description: string;
}

export class HistoryModel implements IHistoryModel {
  previousTransactions: Transaction[] = [];
  futureTransactions: Transaction[] = [];
  currentTransaction: Transaction | null = null;

  constructor() {
    makeAutoObservable(this, {}, { autoBind: true });
  }

  get canUndo() {
    return this.previousTransactions.length > 0;
  }

  get canRedo() {
    return this.futureTransactions.length > 0;
  }

  startTransaction(description: string) {
    return orderedEdit(this, () => this.startNow(description));
  }

  private startNow(description: string) {
    if (this.currentTransaction) {
      // End the previous transaction if one is in progress
      this.endTransaction();
    }
    this.currentTransaction = { actions: [], description };
  }

  add(action: Action) {
    if (!this.currentTransaction) {
      throw new Error('No transaction in progress. Call startTransaction() first.');
    }
    this.currentTransaction.actions.push(action);
  }

  endTransaction() {
    return orderedEdit(this, () => this.endNow());
  }

  private endNow() {
    if (!this.currentTransaction) {
      return;
    }
    if (this.currentTransaction.actions.length > 0) {
      this.previousTransactions.push(this.currentTransaction);
      this.futureTransactions = [];
    }
    this.currentTransaction = null;
  }

  /** Roll back a live gesture without changing either undo or redo history. */
  cancelTransaction() {
    return orderedEdit(this, () => {
      const transaction = this.currentTransaction;
      this.currentTransaction = null;
      if (!transaction) return;
      for (let i = transaction.actions.length - 1; i >= 0; i--)
        transaction.actions[i].undo();
    });
  }

  undo() {
    return orderedEdit(this, () => this.undoNow());
  }

  private undoNow() {
    const timing = performanceDiagnostics.start('editor-sync');
    try {
      this.endTransaction(); // End any in-progress transaction
      if (this.canUndo) {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        const transaction = this.previousTransactions.pop()!;
        // Undo actions in reverse order
        for (let i = transaction.actions.length - 1; i >= 0; i--) {
          transaction.actions[i].undo();
        }
        this.futureTransactions.push(transaction);
      }
    } finally {
      performanceDiagnostics.finish('editor-sync', timing);
    }
  }

  redo() {
    return orderedEdit(this, () => this.redoNow());
  }

  private redoNow() {
    const timing = performanceDiagnostics.start('editor-sync');
    try {
      this.endTransaction(); // End any in-progress transaction
      if (this.canRedo) {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        const transaction = this.futureTransactions.pop()!;
        // Redo actions in forward order
        for (const action of transaction.actions) {
          action.do();
        }
        this.previousTransactions.push(transaction);
      }
    } finally {
      performanceDiagnostics.finish('editor-sync', timing);
    }
  }

  /**
   * Jump to a specific transaction in the history.
   * @param targetIndex The index of the transaction to jump to in the combined history.
   *                  0 = initial state, previousTransactions.length = current state
   */
  jumpToTransaction(targetIndex: number) {
    return orderedEdit(this, () => this.jumpNow(targetIndex));
  }

  private jumpNow(targetIndex: number) {
    this.endTransaction(); // End any in-progress transaction

    const currentIndex = this.previousTransactions.length;
    const totalTransactions =
      this.previousTransactions.length + this.futureTransactions.length;

    if (targetIndex < 0 || targetIndex > totalTransactions) {
      throw new Error(
        `Invalid target index: ${targetIndex}. Must be between 0 and ${totalTransactions}`,
      );
    }

    if (targetIndex === currentIndex) {
      return; // Already at the target
    }

    if (targetIndex < currentIndex) {
      // Need to undo some transactions
      const undoCount = currentIndex - targetIndex;
      for (let i = 0; i < undoCount; i++) {
        this.undoNow();
      }
    } else {
      // Need to redo some transactions
      const redoCount = targetIndex - currentIndex;
      for (let i = 0; i < redoCount; i++) {
        this.redoNow();
      }
    }
  }

  // Legacy properties for backward compatibility
  get previousActions(): Action[] {
    return this.previousTransactions.flatMap((t) => t.actions);
  }

  get futureActions(): Action[] {
    return this.futureTransactions.flatMap((t) => t.actions);
  }
}

export enum ActionType {
  ConfigureTile = 'ConfigureTile',
  ColorTiles = 'ColorTiles',
  MoveTiles = 'MoveTiles',
  EditLabels = 'EditLabels',
  AddTiles = 'AddTiles',
  RemoveTiles = 'RemoveTiles',
  RotateTile = 'RotateTile',
  DisconnectTiles = 'DisconnectTiles',
}

export interface Action {
  isNoop?: boolean;
  type: ActionType;
  description: string;
  do: () => void;
  undo: () => void;
}
